'use client';
import React, { useState, useCallback } from 'react';
import { Search, X } from 'lucide-react';

interface SearchBarProps {
  onSearch?: (query: string) => void;
  placeholder?: string;
  className?: string;
}

export default function SearchBar({
  onSearch,
  placeholder = 'Search headlines, topics, sources…',
  className = '',
}: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setQuery(val);
      // Backend integration: debounced search API call here
      onSearch?.(val);
    },
    [onSearch]
  );

  const handleClear = () => {
    setQuery('');
    onSearch?.('');
  };

  return (
    <div data-component-id="src/components/SearchBar.tsx:35:4" data-component-path="src/components/SearchBar.tsx" data-component-line="35" data-component-end-line="68" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`}>
      <div data-component-id="src/components/SearchBar.tsx:36:6" data-component-path="src/components/SearchBar.tsx" data-component-line="36" data-component-end-line="67" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all duration-200 bg-slate-50 ${
          isFocused
            ? 'border-primary-500 bg-white shadow-sm ring-2 ring-primary-500/15'
            : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <Search data-component-id="src/components/SearchBar.tsx:43:8" data-component-path="src/components/SearchBar.tsx" data-component-line="43" data-component-end-line="48" data-component-file="SearchBar.tsx" data-component-name="Search" data-component-content="%7B%22elementName%22%3A%22Search%22%7D"
          size={16}
          className={`shrink-0 transition-colors duration-150 ${
            isFocused ? 'text-primary-500' : 'text-slate-400'
          }`}
        />
        <input data-component-id="src/components/SearchBar.tsx:49:8" data-component-path="src/components/SearchBar.tsx" data-component-line="49" data-component-end-line="57" data-component-file="SearchBar.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3Aquery%5D%22%2C%22className%22%3A%22flex-1%20bg-transparent%20text-sm%20text-slate-700%20placeholder%3Atext-slate-400%20outline-none%20min-w-0%22%7D"
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none min-w-0"
        />
        {query && (
          <button data-component-id="src/components/SearchBar.tsx:59:10" data-component-path="src/components/SearchBar.tsx" data-component-line="59" data-component-end-line="65" data-component-file="SearchBar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22shrink-0%20p-0.5%20rounded%20text-slate-400%20hover%3Atext-slate-600%20hover%3Abg-slate-200%20transition-colors%20duration-150%22%7D"
            onClick={handleClear}
            className="shrink-0 p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors duration-150"
            aria-label="Clear search"
          >
            <X data-component-id="src/components/SearchBar.tsx:64:12" data-component-path="src/components/SearchBar.tsx" data-component-line="64" data-component-end-line="64" data-component-file="SearchBar.tsx" data-component-name="X" data-component-content="%7B%22elementName%22%3A%22X%22%7D" size={13} />
          </button>
        )}
      </div>
    </div>
  );
}